import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import fetchWithAuth from '../fetchWithAuth';
import './Deals.css';
import NavButtons from './NavButtons';
import BackToTopButton from './BackToTopButton';

const Deals = ({ deals, fetchDeals, navigateTo, goBack, handleDealRedirect, user }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [daysFilter, setDaysFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showDaysDropdown, setShowDaysDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const categoryDropdownRef = useRef(null);
  const daysDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);

  const handleEdit = useCallback((deal) => {
    navigateTo('dealForm', { deal, isEdit: true });
  }, [navigateTo]);

  const handleView = useCallback((deal) => {
    navigateTo('dealDetail', { deal });
  }, [navigateTo]);

  const handleDeactivate = useCallback(async (dealId) => {
    if (user.role !== 'Administrator') {
      toast.error('Only Administrators can deactivate deals.');
      return;
    }
    try {
      console.log(`[${new Date().toISOString()}] Deactivating deal ${dealId}`);
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/deals/${dealId}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, null, 10000);
      if (!response.ok) throw new Error('Failed to deactivate deal');
      await fetchDeals();
      const remainingInactiveDeals = deals.filter(deal => deal._id !== dealId && !deal.active).length;
      const wasInactive = deals.find(deal => deal._id === dealId)?.active === false;
      if (showInactive && wasInactive && remainingInactiveDeals === 0) {
        setShowInactive(false);
      }
      toast.success('Deal status updated successfully');
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Error deactivating deal:`, err);
      toast.error(`Failed to update deal status: ${err.message}`);
    }
  }, [user.role, user.token, fetchDeals, deals, showInactive]);

  const openImageModal = useCallback((imageUrl) => {
    setSelectedImage(imageUrl);
  }, []);

  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const normalizeText = (text) => {
    const smallCapsMap = {
      'ᴛ': 't',
      'ʜ': 'h',
      'ᴇ': 'e',
      'ʙ': 'b',
      'ᴜ': 'u',
      'ʀ': 'r',
      'ɢ': 'g',
      'ᴀ': 'a',
      'ᴘ': 'p',
      'ᴡ': 'w',
    };

    return text
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
      .split('')
      .map(char => smallCapsMap[char] || char)
      .join('')
      .toLowerCase();
  };

  const uniqueCategories = [...new Set(
    deals.flatMap(deal => deal.category.map(cat => (typeof cat === 'object' ? cat.name : cat)))
  )].filter(Boolean).sort();

  const dayOrder = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const uniqueDays = [...new Set(
    deals.flatMap(deal => deal.dayOfWeek)
  )].filter(Boolean).sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

  const uniqueTypes = [...new Set(
    deals.flatMap(deal => deal.type)
  )].filter(Boolean).sort();

  const filteredDeals = deals
    .filter(deal => deal.active === !showInactive)
    .filter(deal => {
      if (!searchQuery) return true;
      const query = normalizeText(searchQuery);
      return (
        normalizeText(deal.title).includes(query) ||
        normalizeText(deal.description).includes(query)
      );
    })
    .filter(deal => {
      if (categoryFilter.length === 0) return true;
      const dealCategories = deal.category.map(cat => (typeof cat === 'object' ? cat.name : cat));
      return categoryFilter.some(cat => dealCategories.includes(cat));
    })
    .filter(deal => {
      if (daysFilter.length === 0) return true;
      return daysFilter.some(day => deal.dayOfWeek.includes(day));
    })
    .filter(deal => {
      if (typeFilter.length === 0) return true;
      return typeFilter.some(type => deal.type.includes(type));
    });

  const hasOppositeDeals = deals.some(deal => deal.active === showInactive);

  const handleToggleDeals = useCallback(() => {
    if (hasOppositeDeals) {
      setShowInactive(prev => !prev);
    }
  }, [hasOppositeDeals]);

  const toggleCategoryFilter = useCallback((category) => {
    setCategoryFilter(prev =>
      prev.includes(category)
        ? prev.filter(cat => cat !== category)
        : [...prev, category]
    );
  }, []);

  const toggleDaysFilter = useCallback((day) => {
    setDaysFilter(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  }, []);

  const toggleTypeFilter = useCallback((type) => {
    setTypeFilter(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  }, []);

  const handleClickOutside = useCallback((event) => {
    if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
      setShowCategoryDropdown(false);
    }
    if (daysDropdownRef.current && !daysDropdownRef.current.contains(event.target)) {
      setShowDaysDropdown(false);
    }
    if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
      setShowTypeDropdown(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  return (
    <div className="deals-page">
      <NavButtons navigateTo={navigateTo} user={user} activePage="deals" />
      {user.role !== 'Light' && (
        <div className="deal-actions">
          <button onClick={() => navigateTo('dealForm')} className="manage-deals">Add New Deal</button>
          <button
            onClick={handleToggleDeals}
            className={`toggle-deals ${!hasOppositeDeals ? 'disabled' : ''}`}
            disabled={!hasOppositeDeals}
          >
            {showInactive ? 'Active Deals' : 'Inactive Deals'}
          </button>
          <input
            type="text"
            placeholder="Search deals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-bar"
          />
        </div>
      )}
      <div className="deals-table-container">
        {filteredDeals && filteredDeals.length > 0 ? (
          <table className="deals-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Description</th>
                <th>
                  Category
                  <button
                    className="filter-button"
                    onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  >
                    ▼
                  </button>
                  {showCategoryDropdown && (
                    <div className="filter-dropdown" ref={categoryDropdownRef}>
                      {uniqueCategories.map(category => (
                        <label key={category} className="filter-option">
                          <input
                            type="checkbox"
                            checked={categoryFilter.includes(category)}
                            onChange={() => toggleCategoryFilter(category)}
                          />
                          {category}
                        </label>
                      ))}
                    </div>
                  )}
                </th>
                <th>
                  Applicable Days
                  <button
                    className="filter-button"
                    onClick={() => setShowDaysDropdown(!showDaysDropdown)}
                  >
                    ▼
                  </button>
                  {showDaysDropdown && (
                    <div className="filter-dropdown" ref={daysDropdownRef}>
                      {uniqueDays.map(day => (
                        <label key={day} className="filter-option">
                          <input
                            type="checkbox"
                            checked={daysFilter.includes(day)}
                            onChange={() => toggleDaysFilter(day)}
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                  )}
                </th>
                <th>Price</th>
                <th>
                  Type
                  <button
                    className="filter-button"
                    onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  >
                    ▼
                  </button>
                  {showTypeDropdown && (
                    <div className="filter-dropdown" ref={typeDropdownRef}>
                      {uniqueTypes.map(type => (
                        <label key={type} className="filter-option">
                          <input
                            type="checkbox"
                            checked={typeFilter.includes(type)}
                            onChange={() => toggleTypeFilter(type)}
                          />
                          {type}
                        </label>
                      ))}
                    </div>
                  )}
                </th>
                <th>Image</th>
                <th>Linked</th>
                <th>Validity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((deal) => (
                <tr key={deal._id}>
                  <td>{deal.title}</td>
                  <td className="description-column">{deal.description}</td>
                  <td>{deal.category && deal.category.length > 0 ? deal.category.map(cat => (typeof cat === 'object' ? cat.name : cat)).join(', ') : 'None'}</td>
                  <td className="days-column">{deal.dayOfWeek.join(', ')}</td>
                  <td>{deal.price ? `R${deal.price}` : 'N/A'}</td>
                  <td>{deal.type.join(', ')}</td>
                  <td>
                    {deal.image ? (
                      <img
                        src={deal.thumbnail || deal.image}  // ← ONLY THIS LINE CHANGED
                        alt={deal.title}
                        className="deal-thumbnail"
                        onClick={() => openImageModal(deal.thumbnail || deal.image)}
                        loading="lazy"
                      />
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{deal.link ? 'Y' : 'N'}</td>
                  <td>{`${new Date(deal.startDate).toLocaleDateString('en-GB')} - ${new Date(deal.endDate).toLocaleDateString('en-GB')}`}</td>
                  <td className="actions-column">
                    <button onClick={() => handleView(deal)} className="action-button view">View</button>
                    {user.role !== 'Light' && (
                      <>
                        <button onClick={() => handleEdit(deal)} className="action-button edit">Edit</button>
                        {user.role === 'Administrator' && (
                          <button onClick={() => handleDeactivate(deal._id)} className="action-button deactivate">
                            {deal.active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>
            {searchQuery
              ? 'No Deals match your Search'
              : `You have no ${showInactive ? 'inactive deals loaded!' : 'active deals loaded.'} ${user.role !== 'Light' && !showInactive && 'Add one now!'}`}
          </p>
        )}
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="Deal" className="modal-image" />
            <button onClick={closeImageModal} className="close-modal">Close</button>
          </div>
        </div>
      )}

      <BackToTopButton />
    </div>
  );
};

export default Deals;